import { useMemo, useState } from "react";

import { ApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useHistorialReinicios } from "@/hooks/useRebootAgencias";
import { usePanelesAcceso, useReiniciarPanel, type PanelAcceso } from "@/hooks/useRebootPaneles";

// Control de Acceso - Holanda (NOVA): estado de los paneles peatonales y
// vehiculares y su reinicio manual. Comparte la bitacora con el reinicio de
// agencias; los paneles quedan ahi como `panel:<ip>`.

function cuando(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function resultado(o: string): { texto: string; tono: "default" | "secondary" | "destructive" } {
  if (o === "ok") return { texto: "Reiniciado", tono: "default" };
  if (o === "denegado_PanelEnEspera") return { texto: "Candado 10 min", tono: "secondary" };
  if (o === "denegado_PanelSinRespuesta") return { texto: "No contestaba", tono: "destructive" };
  if (o === "denegado_PanelRechazo") return { texto: "El panel no aceptó", tono: "destructive" };
  if (o === "denegado_PanelSinCredencial") return { texto: "Sin credencial", tono: "destructive" };
  if (o === "rechazado_lista_blanca") return { texto: "Fuera de lista", tono: "destructive" };
  return { texto: o, tono: "secondary" };
}

function HistorialPaneles({ paneles }: { paneles: PanelAcceso[] }) {
  const { data, isLoading, isError } = useHistorialReinicios();
  const porIp = new Map(paneles.map((p) => [p.ip, p]));
  const intentos = (data?.items ?? []).filter((i) => i.host.startsWith("panel:"));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Historial de reinicios de paneles</CardTitle>
        <CardDescription>
          Si un panel se reinicia y sigue sin contestar, ya no es un panel colgado: hay que
          revisar luz, red o el equipo en sitio.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-muted-foreground">Cargando...</p>}
        {isError && <p className="text-destructive">No se pudo leer el historial.</p>}
        {!isLoading && !isError && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cuándo</TableHead>
                <TableHead>Panel</TableHead>
                <TableHead>Quién</TableHead>
                <TableHead>Resultado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {intentos.map((i, idx) => {
                const ip = i.host.slice("panel:".length);
                const p = porIp.get(ip);
                const r = resultado(i.outcome);
                return (
                  <TableRow key={`${i.ts}-${i.host}-${idx}`}>
                    <TableCell className="whitespace-nowrap">{cuando(i.ts)}</TableCell>
                    <TableCell>{p ? `${p.agencia} · ${p.tipo}` : ip}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{i.actor}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.tono}>{r.texto}</Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
              {intentos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    Todavía no se ha reiniciado ningún panel.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function RebootPanelesPage() {
  const { data, isLoading, isError, error } = usePanelesAcceso();
  const reiniciar = useReiniciarPanel();
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState<"todos" | "up" | "down">("todos");
  const [filtroTipo, setFiltroTipo] = useState<"todos" | "Peatonal" | "Vehicular">("todos");

  const todos = useMemo(() => data?.items ?? [], [data]);
  const paneles = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return todos
      .filter((p) => {
        if (texto && !p.agencia.toLowerCase().includes(texto)) return false;
        if (filtroEstado !== "todos" && p.state !== filtroEstado) return false;
        // El panel de tipo ambiguo (`Panel`) se cuenta con los vehiculares.
        if (filtroTipo === "Peatonal" && p.tipo !== "Peatonal") return false;
        if (filtroTipo === "Vehicular" && p.tipo === "Peatonal") return false;
        return true;
      })
      // Los que no contestan, arriba: son los que hay que mirar.
      .sort((a, b) =>
        a.state === b.state ? a.agencia.localeCompare(b.agencia) : a.state === "down" ? -1 : 1,
      );
  }, [todos, busqueda, filtroEstado, filtroTipo]);

  const vivos = todos.filter((p) => p.state === "up").length;
  const filtrando = busqueda.trim() !== "" || filtroEstado !== "todos" || filtroTipo !== "todos";

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle>Reinicio de paneles de acceso — Holanda</CardTitle>
          <CardDescription>
            El estado se mide al momento. Sólo se puede reiniciar un panel que contesta, y
            una vez cada 10 minutos. Mientras arranca (1-2 min) la puerta o la pluma no
            abren: avisa en la agencia antes de confirmar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-sm text-muted-foreground">Midiendo paneles...</p>}
          {isError && (
            <p className="text-sm text-destructive">
              No se pudieron cargar los paneles:{" "}
              {error instanceof ApiError ? error.message : "error desconocido"}
            </p>
          )}
          {reiniciar.isError && (
            <p className="mb-3 text-sm text-destructive">
              {reiniciar.error instanceof ApiError
                ? reiniciar.error.message
                : "No se pudo reiniciar."}
            </p>
          )}
          {reiniciar.isSuccess && (
            <p className="mb-3 text-sm text-emerald-600">
              Reinicio enviado a {reiniciar.data.nombre}. Vuelve a contestar en 1-2 min.
            </p>
          )}
          {!isLoading && !isError && (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Input
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar agencia..."
                  aria-label="Buscar agencia"
                  className="h-9 w-52"
                />
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value as typeof filtroEstado)}
                  aria-label="Filtrar por estado"
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                >
                  <option value="todos">Todo estado</option>
                  <option value="down">Sólo los que no contestan</option>
                  <option value="up">Sólo los que contestan</option>
                </select>
                <select
                  value={filtroTipo}
                  onChange={(e) => setFiltroTipo(e.target.value as typeof filtroTipo)}
                  aria-label="Filtrar por tipo"
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                >
                  <option value="todos">Peatonal y vehicular</option>
                  <option value="Peatonal">Sólo peatonales</option>
                  <option value="Vehicular">Sólo vehiculares</option>
                </select>
                <span className="ml-auto text-sm text-muted-foreground">
                  {filtrando
                    ? `${paneles.length} de ${todos.length} paneles`
                    : `${vivos} de ${todos.length} contestan`}
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agencia</TableHead>
                    <TableHead>Panel</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Respuesta</TableHead>
                    <TableHead className="text-right">Reinicio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paneles.map((p) => {
                    const enCurso = reiniciar.isPending && reiniciar.variables === p.id;
                    return (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.agencia}</TableCell>
                        <TableCell>
                          {p.tipo}
                          <span className="text-xs text-muted-foreground"> · {p.ip}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={p.state === "up" ? "secondary" : "destructive"}>
                            {p.state === "up" ? "Contesta" : "No contesta"}
                          </Badge>
                        </TableCell>
                        <TableCell>{p.ms !== null ? `${p.ms} ms` : ""}</TableCell>
                        <TableCell className="text-right">
                          {confirmando === p.id ? (
                            <span className="inline-flex flex-wrap items-center justify-end gap-2">
                              <span className="text-xs text-destructive">
                                {p.tipo === "Peatonal" ? "La puerta" : "La pluma"} no abrirá 1-2
                                min.
                              </span>
                              <Button
                                size="sm"
                                variant="destructive"
                                disabled={enCurso}
                                onClick={() => {
                                  reiniciar.mutate(p.id);
                                  setConfirmando(null);
                                }}
                              >
                                Confirmar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setConfirmando(null)}
                              >
                                Cancelar
                              </Button>
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!p.actionable || enCurso}
                              title={p.actionable ? "Reiniciar el panel" : p.reason}
                              onClick={() => setConfirmando(p.id)}
                            >
                              {enCurso ? "Enviando..." : "Reiniciar"}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {paneles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        {filtrando
                          ? "Ningún panel coincide con el filtro."
                          : "No hay paneles en la lista."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
      <HistorialPaneles paneles={todos} />
    </div>
  );
}
